/** @jsx jsx */
import { useMemo, useState, useCallback } from 'react';
import { Controller } from 'react-hook-form';
import cogoToast from 'cogo-toast';
import { css, jsx } from '@emotion/core';
import { useMutation, useQueryClient } from 'react-query';

import { sendErrorService } from '~/services/error';

import { createNewForm } from '~/api/routes/leadad/forms';

import { Form, FormData } from '~/api/models/leadads/form';

import Button from '~/theseus/components/Button';
import Input from '~/theseus/components/Form/Input';
import withError from '~/theseus/components/Form/withError';

import { push } from '~/utils';

import { refreshingSubtle } from '~/theseus/styles/keyframes';
import Field from './Field';
import CallingCard from './CallingCard';
import ContextCards from './ContextCards';
import CaptureFields from './CaptureFields';
import useFormState from './useFormState';
import { usePageQuery } from '~/theseus/hooks/queries';

const InputWithError = withError(Input);

interface Props {
  showBackButton: boolean;
  propertyImages: string[];
  linkType: LinkType;
  link: string;
  onToggleModes: () => void;
  onCreated: (form: Form) => void;
}

type FormRequestData = {
  pageId: string;
  link: string;
  form: FormData;
};

function FormCreator(props: Props) {
  const {
    showBackButton,
    propertyImages,
    onToggleModes,
    onCreated,
    linkType,
    link,
  } = props;

  const [pageId] = usePageQuery();
  const queryClient = useQueryClient();

  const { mutate, isLoading: isCreating } = useMutation((args: FormRequestData) => createNewForm(args.pageId, args.link, args.form), {
    onSuccess: (data) => {
      queryClient.setQueryData(['leadad', 'forms', pageId], push(data));
      cogoToast.success('Successfully created your new lead ad form', { hideAfter: 10 });
      onCreated(data);
    },
    onError: (errResp: ResponseWith<unknown>) => {
      const errorId = sendErrorService(new Error('Problem creating lead ad form'), 'FORMS', errResp.data);
      const { hide } = cogoToast.error(`There was a problem creating your new lead form. Please try again or contact support. Error id: ${errorId}`, {
        hideAfter: 10,
        onClick: () => {
          hide?.();
        },
      });
    },
  });

  const {
    register,
    control,
    handleSubmit,
    errors,
  } = useFormState(linkType);

  const [isReady, setIsReady] = useState(false);

  const isBusy = isCreating || !isReady;

  const create = useCallback((data: FormData) => {
    mutate({
      pageId: pageId as string,
      link,
      form: data,
    });
  }, [mutate, link, pageId]);

  const formLoadingStyles = useMemo(() => {
    if (isCreating) {
      return css`
        animation: ${refreshingSubtle} 1s linear infinite;
        pointer-events: none;
      `;
    }

    return css``;
  }, [isCreating]);

  return (
    <div>
      {
        showBackButton && (
        <Button
          disabled={isCreating}
          variant="plain"
          onClick={onToggleModes}
          size="md"
          css={css`margin-bottom: 20px;`}
        >
          Choose created form
        </Button>
        )
     }
      <div css={formLoadingStyles}>
        <Controller
          control={control}
          name="contextCardId"
          render={({ onChange }) => (
            <ContextCards
              onChange={onChange}
              propertyImages={propertyImages}
              onReady={setIsReady}
            />
          )}
        />
        <form data-testid="lead-ad-form" aria-busy={isBusy} onSubmit={handleSubmit(create)}>
          <CaptureFields register={register} />
          <CallingCard register={register} errors={errors} control={control} />
          <Field htmlFor="form-name" style={{ fontSize: '1.1em' }}>
            <span className="label" style={{ width: 'unset' }}>Form Name</span>
            <InputWithError
              id="form-name"
              name="name"
              ref={register({ required: 'Please enter a form name' })}
              type="text"
              placeholder="Lead Form Name"
              error={errors.name}
              css={{ flexGrow: 2 }}
            />
          </Field>
          <Button
            type="submit"
            size="lg"
            disabled={isBusy}
            style={{ marginTop: 20 }}
          >
            Save and Use Lead Form
          </Button>
        </form>
      </div>
    </div>
  );
}

export default FormCreator;
